import { useState } from "hono/jsx";
import { auth } from "@/features/auth/auth.ts";
import { UserAvatar } from "@/components/common/UserAvatar.tsx";

type User = typeof auth.$Infer.Session.user & {
  theme?: string;
  isAnonymous?: boolean;
  image?: string | null;
  color?: string;
  frame?: string;
};

export const Header = (
  { user, initialTheme }: { user?: User | null; initialTheme?: string },
) => {
  const [theme, setTheme] = useState(initialTheme || "forest");
  const [menuOpen, setMenuOpen] = useState(false);

  const changeTheme = async (newTheme: string) => {
    setTheme(newTheme);
    // @ts-ignore: DOM available in client
    document.documentElement.setAttribute("data-theme", newTheme);

    try {
      await fetch("/api/theme/set", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme: newTheme }),
      });
    } catch (err) {
      console.error("Failed to save theme:", err);
    }
  };

  const themes = [
    "forest",
    "retro",
    "cyberpunk",
    "valentine",
    "aqua",
    "synthwave",
    "light",
    "dark",
    "coffee",
    "cupcake",
  ];

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      <li>
        <a href="/" onClick={() => mobile && setMenuOpen(false)}>Home</a>
      </li>
      <li>
        <a href="/about" onClick={() => mobile && setMenuOpen(false)}>About</a>
      </li>
      {/* Theme Dropdown */}
      <li>
        <details>
          <summary>Theme: {theme}</summary>
          <ul class="bg-base-100 rounded-box z-50 w-full lg:w-48">
            {themes.map((t) => (
              <li>
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    changeTheme(t);
                    if (mobile) setMenuOpen(false);
                  }}
                  class={theme === t ? "active" : ""}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </a>
              </li>
            ))}
          </ul>
        </details>
      </li>
    </>
  );

  const AuthSection = ({ mobile = false }: { mobile?: boolean }) => {
    if (user && !user.isAnonymous) {
      return (
        <div
          class={mobile
            ? "flex flex-col gap-2 p-4 border-t border-base-300"
            : "dropdown dropdown-end ml-4"}
        >
          {mobile
            ? (
              <>
                <div class="font-semibold">{user.name || "User"}</div>
                <a
                  href="/profile"
                  class="btn btn-ghost btn-sm justify-start"
                  onClick={() => setMenuOpen(false)}
                >
                  Profile
                </a>
                <a
                  href="/logout"
                  class="btn btn-ghost btn-sm text-error justify-start"
                  onClick={() => setMenuOpen(false)}
                >
                  Logout
                </a>
              </>
            )
            : (
              <>
                <div
                  tabindex={0}
                  role="button"
                  class="btn btn-ghost btn-circle avatar online"
                >
                  <UserAvatar user={user} />
                </div>
                <ul
                  tabindex={0}
                  class="mt-3 z-50 p-2 shadow menu menu-sm dropdown-content bg-base-100 rounded-box w-52"
                >
                  <li class="menu-title">Account</li>
                  <li>
                    <a href="/profile">Profile</a>
                  </li>
                  <li>
                    <a href="/logout" class="text-error">Logout</a>
                  </li>
                </ul>
              </>
            )}
        </div>
      );
    }
    return (
      <div
        class={mobile
          ? "flex flex-col gap-2 p-4 border-t border-base-300"
          : "flex gap-2 ml-4"}
      >
        <a
          href="/login"
          class="btn btn-ghost btn-sm"
          onClick={() => mobile && setMenuOpen(false)}
        >
          Login
        </a>
        <a
          href="/register"
          class="btn btn-primary btn-sm"
          onClick={() => mobile && setMenuOpen(false)}
        >
          Register
        </a>
      </div>
    );
  };

  return (
    <div class="navbar py-0 bg-base-100 shadow-lg mb-4 rounded-box">
      {/* Logo */}
      <div class="flex-1">
        <a class="btn btn-ghost text-xl" href="/">The Farm</a>
      </div>

      {/* Desktop nav */}
      <div class="flex-none hidden lg:flex items-center">
        <ul class="menu menu-horizontal px-1 items-center gap-2">
          <NavLinks />
        </ul>
        <AuthSection />
      </div>

      {/* Mobile: Avatar menu trigger on the right */}
      <div class="flex-none lg:hidden">
        {user && !user.isAnonymous
          ? (
            <button
              type="button"
              class="btn btn-ghost btn-circle avatar"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <UserAvatar user={user} />
            </button>
          )
          : (
            <button
              type="button"
              class="btn btn-ghost btn-circle"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                class="w-6 h-6 stroke-current"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </button>
          )}
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div class="absolute top-full left-0 right-0 z-50 lg:hidden bg-base-100 shadow-lg rounded-b-box">
          <ul class="menu menu-vertical w-full p-4">
            <NavLinks mobile />
          </ul>
          <AuthSection mobile />
        </div>
      )}
    </div>
  );
};
